import { useContext, useEffect, useState } from 'react'
import CustomButton from '../../../components/Buttons/CustomButton'
import { Dialog } from 'primereact/dialog'
import { Button } from 'primereact/button'
import { useLazyQuery } from "@apollo/client"
import { UserContext } from '../../../context/userContext'
import _ from 'lodash'
import FormSummary from './FormSummary'
import { LogDebug, LogError } from '../../../helpers/logger'
import { classNames } from 'primereact/utils'

// Queries
import { ENGAGEMENTS_PARTICIPANT_GROUP_BY_TEMPLATE } from '../../../pages/Engagements/queries'
import { GET_TEMPLATE_PAGE } from '../queries'

// Styles
import styles from "../../../styles/Form.module.css"
import { STAFF_SUPERVISION_GROUP_BY_TEMPLATE } from '../../Staff/queries'

export default function PreviousSession({ templateId, templateType, templateTitle, blocks }) {
  // Data state
  const userCtx = useContext(UserContext)
  const [prevSessionData, setPrevSessionData] = useState(null) // This will hold the transformed data

  // Display state
  const [showPrevSessionBtn, setShowPrevSessionBtn] = useState(false)
  const [loadingData, setLoadingData] = useState(false)
  const [showPrevSession, setShowPrevSession] = useState(false)

  const [isCollapsed, setIsCollapsed] = useState(false)

  // Query to fetch previous session values for: participant
  const [fetchPrevSessionDataParticipant] = useLazyQuery(
    ENGAGEMENTS_PARTICIPANT_GROUP_BY_TEMPLATE, {
    fetchPolicy: "no-cache",
  })

  // Query to fetch previous session values for: individual_supervision_session
  const [fetchPrevSessionDataStaffSupervision] = useLazyQuery(
    STAFF_SUPERVISION_GROUP_BY_TEMPLATE, {
    fetchPolicy: "no-cache",
  })

  // Query to fetch template config
  const [fetchTemplateConfig] = useLazyQuery(GET_TEMPLATE_PAGE)

  // Fetch previous session data with template config and transform for FormSummary
  async function fetchData() {
    try {
      const isStaffSupervisionSession = templateId === "individual_supervision_session"
      let sessionData = null
      if (isStaffSupervisionSession) {
        // Fetch previous session data for staff (e.g. supervision session)
        const res = await fetchPrevSessionDataStaffSupervision({
          variables: {
            staff_membership_id: userCtx.current_staff_membership_id,
            organization_id: userCtx.session.user.organization_id,
            idTemplate: templateId,
            idBlocks: blocks.map(block => block.block_name),
          }
        })

        // Check if the response contains the expected data structure
        if (res?.data?.staffSupervision?.data) {
          sessionData = res.data.staffSupervision.data
        }
      }
      else {
        // Fetch previous session data for participant
        const res = await fetchPrevSessionDataParticipant({
          variables: {
            participant_id: userCtx.participant_id,
            organization_id: userCtx.session.user.organization_id,
            idTemplates: [templateId],
            idBlocks: blocks.map(block => block.block_name),
          }
        })

        // Check if the response contains the expected data structure
        if (res?.data?.engagementsParticipant?.data) {
          sessionData = res.data.engagementsParticipant.data
        }

      }

      if (sessionData) {
        // Parse the session data which
        const rawSessionValues = JSON.parse(sessionData)

        // Find the most recent session based on the latest date_modified value inside its blocks.
        const sessions = _.values(rawSessionValues)
        if (sessions.length === 0) {
          LogDebug("No previous session data found.")
          setShowPrevSessionBtn(false)
          setLoadingData(false)
          return
        }

        // Sort sessions to find the most recent one. The "latest" session is determined
        // by the most recent 'date_modified' timestamp found within any of its blocks.
        sessions.sort((a, b) => {
          // Find the latest modification date within each session being compared.
          const maxDateA = Math.max(...a.map(block => block.date_modified?._seconds || 0))
          const maxDateB = Math.max(...b.map(block => block.date_modified?._seconds || 0))
          // Sort in descending order to bring the most recent session to the front.
          return maxDateB - maxDateA
        })

        // The most recent session will be the first element after sorting.
        const latestSessionData = sessions[0]

        if (!latestSessionData) {
          LogDebug("No previous session data found.")
          setShowPrevSessionBtn(false)
          setLoadingData(false)
          return
        }

        // Now that we have the latest session data, lets fetch the template config
        // This is intentionally delayed until confirmed there are previous session values
        if (latestSessionData?.length > 0) {
          /*
          Note: While the parent component also fetches template/block config data,
          it does it one block at a time, I need the entire template (all blocks) at once.
          */

          // Fetch template config
          const templateData = await fetchTemplateConfig({
            variables: {
              staff_id: userCtx.session.user.staff_id,
              organization_id: userCtx.session.user.organization_id,
              enterprise_id: userCtx.session.user.enterprise_id,
              type: "system",
              participant_id: userCtx.participant_id,
              idTemplate: templateId,
              editMode: false
            }
          })

          if (templateData?.data) {
            // Transform data and update state
            const { globalValues, blocksFetched } = transformDataForSummary(latestSessionData, templateData.data.template)
            setPrevSessionData({ globalValues, blocksFetched })
            setLoadingData(false)
          }
          else {
            LogDebug("No template data found")
            setShowPrevSessionBtn(false)
            setLoadingData(false)
          }
        }
      }
      else {
        LogDebug(`No session data found for ${isStaffSupervisionSession ? "staff supervision session" : "participant session"}`)
        setShowPrevSessionBtn(false)
        setLoadingData(false)
      }
    } catch (error) {
      LogError("Error fetching previous session data", error)
      setShowPrevSessionBtn(false)
      setLoadingData(false)
    }
  }

  // Transforms the data into a structure for FormSummary.
  function transformDataForSummary(sessionData, templateConfig) {
    const globalValues = {}

    // First, populate globalValues
    for (const block of sessionData) {
      globalValues[block.block_name] = block.values
    }

    // Then, map template blocks to the format FormSummary expects
    const blocksFetched = templateConfig.blocks.map(blockDef => {
      return {
        ...blockDef,
        fields: blockDef.fields || []
      } // Ensure we have a mutable copy
    })

    // Find the session block in the original data to get staff_full_name
    const sessionBlockData = sessionData.find(b => b.block_name === 'session')
    if (sessionBlockData && sessionBlockData.staff_full_name) {
      // Inject the facilitating_staff value into globalValues for the session block
      if (!globalValues.session) {
        globalValues.session = {}
      }
      globalValues.session.facilitating_staff = sessionBlockData.staff_full_name

      // Inject the field definition for facilitating_staff into the session block's fields
      const sessionBlockDef = blocksFetched.find(b => b.block_name === 'session')
      if (sessionBlockDef) {
        // Create a mutable copy of the fields array before pushing to it
        sessionBlockDef.fields = [...sessionBlockDef.fields, { field_name: 'facilitating_staff', field_label: 'Facilitating Staff', field_type: 'text' }]
      }
    }

    return { globalValues, blocksFetched }
  }

  useEffect(() => {
    // Confirm we have values for all the required variables
    if ((!userCtx.participant_id && !userCtx.current_staff_membership_id) ||
      !userCtx.session.user.staff_id || !userCtx.session.user.organization_id ||
      !userCtx.session.user.enterprise_id || !templateId || !blocks) {
      LogDebug("Missing required variables for fetching previous session data, skipping fetch", {
        participant_id: userCtx.participant_id,
        current_staff_membership_id: userCtx.current_staff_membership_id,
        enterprise_id: userCtx.session.user.enterprise_id,
        organization_id: userCtx.session.user.organization_id,
        staff_id: userCtx.session.user.staff_id,
        templateId,
        blocks
      })
      return
    }

    // Confirm template-type requirements
    if (templateType !== "session_module") {
      return
    }

    // Initiate data fetch...
    setLoadingData(true)
    setShowPrevSessionBtn(true) // Show the button in a loading state
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userCtx.participant_id,
    userCtx.session.user.staff_id,
    userCtx.session.user.organization_id,
    userCtx.session.user.enterprise_id,
    templateId,
    templateType,
    blocks,
  ])

  if (!showPrevSessionBtn) return null

  // Toggles the collapsed/expanded state of the dialog
  const toggleCollapse = () => setIsCollapsed(!isCollapsed)

  // Custom header with a collapse/expand button
  const headerTemplate = (
    <div className={styles.dialogHeader}>
      {!isCollapsed && <span>{`Previous: ${templateTitle}`}</span>}
      <div>
        <Button
          icon={isCollapsed ? "pi pi-window-maximize" : "pi pi-window-minimize"}
          className={classNames("p-button-text", styles.prevSessionToggleBtn)}
          onClick={toggleCollapse}
          tooltip={isCollapsed ? "Expand Previous Session" : "Collapse Previous Session"}
          tooltipOptions={{ position: 'left' }}
        />
        {!isCollapsed && (
          <Button
            icon="pi pi-times"
            className="p-button-text"
            onClick={() => setShowPrevSession(false)}
            tooltip="Close Previous Session"
            tooltipOptions={{ position: 'left' }}
          />
        )}
      </div>
    </div>
  )

  const dialogClassName = `${styles.prevSessionModal} ${isCollapsed ? styles.prevSessionModalCollapsed : ''}`

  return (
    <>
      <div className={styles.prevSessionContainer}>
        <CustomButton
          label="View Previous Session"
          icon="pi pi-history"
          iconPos="left"
          className={styles.prevSessionBtn}
          severity="secondary"
          onClick={() => {
            setShowPrevSession(true)
            setIsCollapsed(false) // Always open in expanded state
          }}
          disabled={loadingData || !prevSessionData || showPrevSession}
          loading={loadingData}
        />
      </div>

      <Dialog
        visible={showPrevSession}
        onHide={() => setShowPrevSession(false)}
        header={headerTemplate}
        position="bottom-right"
        className={dialogClassName}
        closable={false}
        modal={false}
        resizable={false}
        draggable={false}
      >
        {prevSessionData && (
          <div hidden={isCollapsed}>
            <FormSummary
              blocksFetched={prevSessionData.blocksFetched}
              globalValues={prevSessionData.globalValues}
              prevSessionSummary={true}
            />
          </div>
        )}
      </Dialog>
    </>
  )
}
